import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { formatDate, getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { DcrSheetSearch } from '../types/product.types';
import { toSheetFilterSearch, toSheetFilterValues } from '../utils/productFilters';

const ROUTES = {
  sheet: getRouteApi('/_auth/product/dcr-sheet'),
  range: getRouteApi('/_auth/product/dcr-date-to-date'),
} as const;

export type SheetRouteId = keyof typeof ROUTES;

function getSheetDefaultPeriod(routeId: SheetRouteId) {
  if (routeId === 'range') return getMonthToDateRange();
  const today = formatDate(new Date());
  return { dateFrom: today, dateTo: today };
}

export function useSheetFilters(routeId: SheetRouteId) {
  const route = ROUTES[routeId];
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const isSingleDay = routeId === 'sheet';

  const defaultPeriod = useMemo(() => getSheetDefaultPeriod(routeId), [routeId]);
  const appliedValues = useMemo(
    () => toSheetFilterValues(search, defaultPeriod),
    [search, defaultPeriod],
  );

  const normalizeFilterValues = useCallback(
    (values: Record<string, string>) =>
      toSheetFilterValues(toSheetFilterSearch(values, isSingleDay), defaultPeriod),
    [isSingleDay, defaultPeriod],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues: defaultPeriod,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...toSheetFilterSearch(values, isSingleDay), q: previous.q }),
        replace: true,
      });
    },
  });

  const applySearchText = (text: string | undefined) => {
    void navigate({ search: (previous) => ({ ...previous, q: text }), replace: true });
  };

  const params = useMemo(
    (): DcrSheetSearch & { dateFrom: string; dateTo: string } => ({
      userId: search.userId,
      dateFrom: search.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: search.dateTo ?? defaultPeriod.dateTo,
    }),
    [search, defaultPeriod],
  );

  return {
    ...filters,
    isSingleDay,
    params,
    dcrTypeId: search.dcrTypeId,
    appliedSearchText: search.q,
    applySearchText,
  };
}
